import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import info from '../../assets/info-icon.svg';
import DeleteImage from '../../assets/delete-img-modal.svg';
import {
  addIbpGdoDependency,
  fetchActiveIbpGdoList,
  fetchBulkUploadLookups,
  getIbpGdoInfo,
  removeIbpGdoDependency,
} from 'service/service';

interface GdoRelationship {
  id: string;
  name: string;
  market?: string;
  dependencyType?: 'successor' | 'predecessor';
  isHardDependency?: boolean;
}

type LoadedGdoData = {
  infoData: any;
  successors: GdoRelationship[];
  predecessors: GdoRelationship[];
  gdoOptions: GdoRelationship[];
  markets: string[];
};

const toGdoRelationship = (
  item: any,
  dependencyType: 'successor' | 'predecessor',
): GdoRelationship => ({
  id: item?.dependsOnProgram || item?.programId || '',
  name: item?.dependsOnProgramName || item?.programName || '',
  market: item?.market,
  dependencyType,
  isHardDependency: item?.isHardDependency ?? true,
});

const getUniqueRelationships = (
  items: any[],
  dependencyType: 'successor' | 'predecessor',
) => {
  const relationships = items.map((item) => toGdoRelationship(item, dependencyType));
  return relationships.filter(
    (relationship, index, all) =>
      relationship.id && all.findIndex((candidate) => candidate.id === relationship.id) === index,
  );
};

const getGdoOptionsFromList = (records: any[]): GdoRelationship[] =>
  records
    .map((gdo: any): GdoRelationship => ({
      id: gdo?.programId || gdo?.programID || gdo?.Program_id || gdo?.id || '',
      name: gdo?.programName || gdo?.Program_Name || gdo?.ProgramName || '',
    }))
    .filter((gdo: GdoRelationship) => gdo.id && gdo.name);

const getUniqueGdoOptions = (relationships: GdoRelationship[]) =>
  relationships.filter(
    (relationship, index, all) =>
      all.findIndex((candidate) => candidate.id === relationship.id) === index,
  );

const loadProgramTabData = async (
  sourceId: string,
): Promise<LoadedGdoData | null> => {
  if (!sourceId) return null;

  const infoResult = await getIbpGdoInfo(sourceId);
  const infoData = infoResult?.data || {};
  const successors = getUniqueRelationships(infoData.successors || [], 'successor');
  const predecessors = getUniqueRelationships(infoData.predecessors || [], 'predecessor');
  const gdoOptionsFromInfo = getUniqueGdoOptions([...successors, ...predecessors]);
  const [activeGdoResult, lookupResult] = await Promise.all([
    fetchActiveIbpGdoList(),
    fetchBulkUploadLookups(),
  ]);
  const activeGdoData = activeGdoResult?.data || activeGdoResult || [];
  const gdoOptionsFromList = Array.isArray(activeGdoData)
    ? getGdoOptionsFromList(activeGdoData)
    : [];
  const lookupData = lookupResult?.data || lookupResult || {};
  const lookupMarkets = Array.isArray(lookupData.markets) ? lookupData.markets : [];
  const relationshipMarkets = [...successors, ...predecessors]
    .map((relationship) => relationship.market)
    .filter((market): market is string => Boolean(market));

  return {
    infoData,
    successors,
    predecessors,
    gdoOptions: gdoOptionsFromList.length > 0 ? gdoOptionsFromList : gdoOptionsFromInfo,
    markets: Array.from(new Set([...lookupMarkets, ...relationshipMarkets])),
  };
};

const getProgramValidationError = (program: string, isGdoMode: boolean, programError: string) => {
  const trimmedProgram = program?.trim() || '';
  if (programError) return programError;
  if (!trimmedProgram) return 'GDO name is required.';
  if (isGdoMode && !trimmedProgram.startsWith('GDO-')) {
    return 'GDO name must start with "GDO-".';
  }
  if (trimmedProgram.length > 64) return 'GDO name cannot exceed 64 characters.';
  return '';
};

const isInvalidProgramName = (program: string, isGdoMode: boolean, programError: string) => {
  const trimmedProgram = program?.trim() || '';
  return Boolean(getProgramValidationError(trimmedProgram, isGdoMode, programError));
};

export const ProgramTab = ({
  sourceData,
  setSourceData,
  programOptions,
  sectorOptions,
  isGdoMode = false,
  programError = '',
  setProgramError = () => {},
}: any) => {
  const theme = useTheme();

  const [gdoOptions, setGdoOptions] = useState<GdoRelationship[]>([]);
  const [marketOptions, setMarketOptions] = useState<string[]>([]);
  const [relationshipError, setRelationshipError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    field: 'successors' | 'predecessors';
    relationship: GdoRelationship | null;
  }>({
    open: false,
    field: 'successors',
    relationship: null,
  });
  const [relationshipDialog, setRelationshipDialog] = useState<{
    open: boolean;
    field: 'successors' | 'predecessors';
    selectedGdo: string;
    selectedMarket: string;
    dependencyType: string;
    isHardDependency: boolean;
  }>({
    open: false,
    field: 'successors',
    selectedGdo: '',
    selectedMarket: '',
    dependencyType: 'Hard',
    isHardDependency: true,
  });

  useEffect(() => {
    let cancelled = false;

    const loadGdoData = async () => {
      const loadedData = await loadProgramTabData(sourceData?.id);
      if (cancelled || !loadedData) return;

      setSourceData((currentData: any) => ({
        ...currentData,
        program: loadedData.infoData.programName || currentData.program || '',
        description: loadedData.infoData.programDescription || currentData.description || '',
        successors: loadedData.successors,
        predecessors: loadedData.predecessors,
      }));
      setGdoOptions(loadedData.gdoOptions);
      setMarketOptions(Array.from(new Set([...loadedData.markets, sourceData.market].filter(Boolean))));
    };

    void loadGdoData();
    return () => {
      cancelled = true;
    };
  }, [sourceData?.id]);

  const trimmedProgram = sourceData.program?.trim() || '';
  const isError = isInvalidProgramName(trimmedProgram, isGdoMode, programError);
  const helperText = getProgramValidationError(trimmedProgram, isGdoMode, programError);

  const updateField = (field: string, value: any) => {
    setSourceData({ ...sourceData, [field]: value });
    if (field === 'program' && programError) {
      setProgramError('');
    }
  };

  const openDeleteDialog = (field: 'successors' | 'predecessors', relationship: GdoRelationship) => {
    setRelationshipError('');
    setSuccessMessage('');
    setDeleteDialog({ open: true, field, relationship });
  };

  const removeRelationship = async () => {
    const { field, relationship } = deleteDialog;
    if (!relationship) return;

    if (sourceData.id) {
      const result = await removeIbpGdoDependency({
        programId: sourceData.id,
        programName: sourceData.program,
        dependsOnProgram: relationship.id,
        dependsOnProgramName: relationship.name,
        market: relationship.market || sourceData.market || marketOptions[0],
        businessProgram: 'IBP',
      });
      if (result?.success === false || result?.statusCode >= 400) {
        setRelationshipError(result?.data?.message || result?.message || 'Unable to remove dependency.');
        return;
      }
    }

    updateField(field, (sourceData[field] || []).filter((item: GdoRelationship) => item.id !== relationship.id));
    setDeleteDialog({ open: false, field: 'successors', relationship: null });
    setSuccessMessage(`${field === 'successors' ? 'Successor' : 'Predecessor'} deleted successfully.`);
  };

  const openRelationshipDialog = (field: 'successors' | 'predecessors') => {
    setRelationshipError('');
    setSuccessMessage('');
    const linkedGdoIds = new Set([
      ...(sourceData.successors || []),
      ...(sourceData.predecessors || []),
    ].map((item: GdoRelationship) => item.id));
    const firstAvailable = gdoOptions.find(
      (option) => option.id !== sourceData.id && !linkedGdoIds.has(option.id),
    );
    setRelationshipDialog({
      open: true,
      field,
      selectedGdo: firstAvailable?.id || '',
      selectedMarket: sourceData.market || marketOptions[0] || '',
      dependencyType: 'Hard',
      isHardDependency: true,
    });
  };

  const linkedGdoIds = new Set([
    ...(sourceData.successors || []),
    ...(sourceData.predecessors || []),
  ].map((item: GdoRelationship) => item.id));
  const availableGdoOptions = gdoOptions.filter(
    (option) => option.id !== sourceData.id && !linkedGdoIds.has(option.id),
  );

  const saveRelationship = async () => {
    const { field, selectedGdo, selectedMarket, isHardDependency } = relationshipDialog;
    const currentValues = sourceData[field] || [];
    const selected = gdoOptions.find((option) => option.id === selectedGdo);
    if (!selected || !selectedMarket) return;

    if (sourceData.id) {
      const result = await addIbpGdoDependency({
        programId: sourceData.id,
        programName: sourceData.program,
        dependsOnProgram: selected.id,
        dependsOnProgramName: selected.name,
        market: selectedMarket,
        businessProgram: 'IBP',
        dependencyType: field === 'successors' ? 'successor' : 'predecessor',
        isHardDependency,
      });
      if (result?.success === false || result?.statusCode >= 400) {
        setRelationshipError(result?.data?.message || result?.message || 'Unable to add dependency.');
        return;
      }
    }

    if (currentValues.some((item: GdoRelationship) => item.id === selected.id)) {
      setRelationshipDialog((current) => ({ ...current, open: false }));
      return;
    }

    updateField(field, [...currentValues, {
      ...selected,
      market: selectedMarket,
      dependencyType: field === 'successors' ? 'successor' : 'predecessor',
      isHardDependency,
    }]);
    setRelationshipDialog((current) => ({ ...current, open: false }));
    setSuccessMessage(`${field === 'successors' ? 'Successor' : 'Predecessor'} added successfully.`);
  };

  const inputSx = {
    width: '100%',
    backgroundColor: '#F4F8FC',
    borderRadius: '4px',
    '& .MuiInputBase-input': {
      color: '#003771 !important',
      WebkitTextFillColor: '#003771 !important',
    },
    '& .MuiInputLabel-root': {
      color: '#5A6A85 !important',
      '&.Mui-focused': { color: '#003771 !important' },
      '&.MuiInputLabel-shrink': { color: '#003771 !important' },
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#F4F8FC',
      '& fieldset': { borderColor: '#71758FCC !important' },
      '&:hover fieldset': { borderColor: '#009BD8 !important' },
      '&.Mui-focused fieldset': { borderColor: '#009BD8 !important' },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#71758FCC !important',
    },
    '& .MuiSvgIcon-root': { color: '#009BD8 !important' },
    '& .MuiSelect-icon': { color: '#009BD8 !important' },
    '& .MuiSelect-select': {
      color: '#003771 !important',
      WebkitTextFillColor: '#003771 !important',
    },
  };

  const selectMenuProps = {
    PaperProps: {
      sx: {
        backgroundColor: '#FFFFFF !important',
        color: '#003771 !important',
        border: '1px solid #D2E6F9',
        boxShadow: '0px 4px 16px rgba(0, 55, 113, 0.08)',
        '& .MuiList-root, & .MuiMenu-list': {
          backgroundColor: '#FFFFFF !important',
          paddingTop: 0,
          paddingBottom: 0,
        },
        '& .MuiMenuItem-root': {
          color: '#003771 !important',
          backgroundColor: '#FFFFFF !important',
          '&:hover': { backgroundColor: '#F4F8FC !important' },
          '&.Mui-selected': { backgroundColor: '#D8F0FA !important' },
          '&.Mui-selected:hover': { backgroundColor: '#D8F0FA !important' },
        },
      },
    },
  };

  const gdoSelectMenuProps = {
    ...selectMenuProps,
    PaperProps: {
      ...selectMenuProps.PaperProps,
      sx: {
        ...selectMenuProps.PaperProps.sx,
        width: 258,
        maxHeight: 240,
        overflowY: 'auto',
      },
    },
  };

  const relationshipSx = {
    minWidth: 114,
    height: '40px !important',
    minHeight: '40px !important',
    maxHeight: '40px !important',
    borderRadius: '3px',
    border: '1px solid #9BC9E8 !important',
    backgroundColor: '#D9ECFA !important',
    color: '#0F4B7D !important',
    '& .MuiChip-label': {
      color: '#0F4B7D !important',
      flexGrow: 1,
      fontSize: '14px',
      fontWeight: 500,
      padding: '0 8px',
      lineHeight: '40px',
      textAlign: 'left',
    },
    '& .MuiChip-deleteIcon': {
      color: '#F04444 !important',
      fontSize: 19,
      marginRight: '5px',
      marginLeft: 0,
    },
  };

  if (isGdoMode) {
    return (
      <Box sx={{ mt: 3, px: { xs: 2, md: 4 }, pb: 4 }}>
        <Snackbar
          open={Boolean(successMessage)}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccessMessage('')} severity="success" variant="filled">
            {successMessage}
          </Alert>
        </Snackbar>
        <Typography
          variant="body2"
          sx={{
            opacity: 0.7,
            mb: 4,
            color: 'textLight.main',
            display: 'flex',
            gap: '4px',
            fontStyle: 'italic',
          }}
        >
          <img src={info} alt="" /> This section is to configure GDO.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="subHeader" sx={{ color: '#009BD8', fontWeight: 600, whiteSpace: 'nowrap' }}>
            GDO Name
          </Typography>
          <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8' }} />
        </Box>

        <Grid container spacing={2} sx={{ width: { xs: '100%', md: '75%' } }}>
          <Grid item xs={12} md={12}>
            <FormControl fullWidth error={isError}>
              <TextField
                label="GDO Name *"
                placeholder="GDO-"
                value={sourceData.program || ''}
                onChange={(event) => updateField('program', event.target.value)}
                InputProps={{ readOnly: true }}
                size="small"
                error={isError}
                helperText={helperText}
                InputLabelProps={{
                  sx: {
                    color: '#5A6A85 !important',
                    '&.Mui-focused, &.MuiInputLabel-shrink': { color: '#003771 !important' },
                  },
                }}
                sx={{
                  ...inputSx,
                  '& .MuiFormHelperText-root': { color: theme.palette.error.main },
                  ...(isError && {
                    '& .MuiOutlinedInput-root fieldset': {
                      borderColor: `${theme.palette.error.main} !important`,
                    },
                  }),
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12}>
            <TextField
              fullWidth
              size="small"
              label="GDO Description"
              value={sourceData.description || ''}
              onChange={(event) => updateField('description', event.target.value)}
              sx={inputSx}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          {relationshipError && (
            <Typography sx={{ color: theme.palette.error.main, mb: 2 }}>
              {relationshipError}
            </Typography>
          )}
          {(['successors', 'predecessors'] as const).map((field) => {
            const label = field === 'successors' ? 'Successor' : 'Predecessor';
            return (
              <Box key={field} sx={{ mb: 3 }}>
                <Typography sx={{ mb: 1.25, color: 'textLight.dark', fontWeight: 500 }}>
                  {label}
                </Typography>
                <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
                  {(sourceData[field] || []).map((relationship: GdoRelationship, index: number) => (
                    <Tooltip key={relationship.id} title={relationship.name} arrow>
                      <Chip
                        label={relationship.name}
                        deleteIcon={<CloseIcon aria-hidden="true" />}
                        onDelete={() => openDeleteDialog(field, relationship)}
                        sx={{
                          ...relationshipSx,
                          borderColor: index === 0 ? '#3C83B5 !important' : 'transparent !important',
                        }}
                      />
                    </Tooltip>
                  ))}
                  {availableGdoOptions.length > 0 && <Button
                    aria-label={`Add ${label}`}
                    title={`Add ${label}`}
                    onClick={() => openRelationshipDialog(field)}
                    sx={{
                      minWidth: 32,
                      width: 32,
                      height: 32,
                      p: 0,
                      color: '#009BD8 !important',
                      fontSize: 32,
                      lineHeight: 1,
                      fontWeight: 300,
                      '&:hover': { backgroundColor: 'transparent' },
                    }}
                  >
                    +
                  </Button>}
                </Stack>
              </Box>
            );
          })}
        </Box>

        <Dialog
          open={relationshipDialog.open}
          onClose={() => setRelationshipDialog((current) => ({ ...current, open: false }))}
          sx={{
            '& .MuiDialog-container': {
              justifyContent: 'center',
              alignItems: 'center',
              pl: 0,
              pt: 0,
            },
          }}
          PaperProps={{
            sx: {
              width: 290,
              minHeight: 500,
              maxWidth: 'calc(100% - 32px)',
              m: 0,
              borderRadius: 0,
              backgroundColor: '#F9FCFF !important',
              color: '#003771 !important',
              '& .MuiDialogTitle-root': {
                backgroundColor: '#F9FCFF !important',
                color: '#003771 !important',
              },
              boxShadow: '0 8px 24px rgba(0, 55, 113, 0.18)',
            },
          }}
        >
          <DialogTitle
            sx={{
              px: 2,
              py: 0.5,
              minHeight: 32,
              backgroundColor: '#F9FCFF !important',
              color: '#003771 !important',
              fontSize: '14px',
              fontWeight: 600,
              position: 'relative',
              mx: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 16,
                right: 16,
                bottom: 0,
                height: '1px',
                backgroundColor: '#003771',
              },
            }}
          >
            Add {relationshipDialog.field === 'successors' ? 'Successor' : 'Predecessor'}
            <IconButton
              size="small"
              aria-label="Close relationship dialog"
              onClick={() => setRelationshipDialog((current) => ({ ...current, open: false }))}
              sx={{
                color: '#003771 !important',
                p: 0,
                '& .MuiSvgIcon-root': { color: '#003771 !important' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 2, py: 2, color: '#003771 !important' }}>
            <Typography sx={{ color: '#71758F !important', fontSize: '11px', mt: 1.5, mb: 0.5 }}>
              Select GDO
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={relationshipDialog.selectedGdo}
              onChange={(event) =>
                setRelationshipDialog((current) => ({ ...current, selectedGdo: event.target.value }))
              }
              sx={{
                ...inputSx,
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#EDF4FD !important',
                  '& fieldset': { borderColor: '#EDF4FD !important' },
                  '&:hover fieldset': { borderColor: '#009BD8 !important' },
                  '&.Mui-focused fieldset': { borderColor: '#009BD8 !important' },
                },
              }}
              SelectProps={{ MenuProps: gdoSelectMenuProps }}
            >
              {availableGdoOptions.map((gdo) => (
                <MenuItem key={gdo.id} value={gdo.id}>{gdo.name}</MenuItem>
                ))}
            </TextField>

            <Typography sx={{ color: '#71758F !important', fontSize: '11px', mb: 0.5 }}>
              Select Dependency Type
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={relationshipDialog.dependencyType}
              onChange={(event) =>
                setRelationshipDialog((current) => ({
                  ...current,
                  dependencyType: event.target.value,
                  isHardDependency: event.target.value === 'Hard',
                }))
              }
              sx={{
                ...inputSx,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#EDF4FD !important',
                  '& fieldset': { borderColor: '#EDF4FD !important' },
                  '&:hover fieldset': { borderColor: '#009BD8 !important' },
                  '&.Mui-focused fieldset': { borderColor: '#009BD8 !important' },
                },
              }}
              SelectProps={{ MenuProps: selectMenuProps }}
            >
              <MenuItem value="Hard">Hard</MenuItem>
              <MenuItem value="Soft">Soft</MenuItem>
            </TextField>

            <Typography sx={{ color: '#71758F !important', fontSize: '11px', mt: 2, mb: 0.5 }}>
              Select Market
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={relationshipDialog.selectedMarket}
              onChange={(event) =>
                setRelationshipDialog((current) => ({ ...current, selectedMarket: event.target.value }))
              }
              sx={{
                ...inputSx,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#EDF4FD !important',
                  '& fieldset': { borderColor: '#EDF4FD !important' },
                  '&:hover fieldset': { borderColor: '#009BD8 !important' },
                  '&.Mui-focused fieldset': { borderColor: '#009BD8 !important' },
                },
              }}
              SelectProps={{ MenuProps: selectMenuProps }}
            >
              {marketOptions.map((market) => (
                <MenuItem key={market} value={market}>{market}</MenuItem>
              ))}
            </TextField>

            <Divider sx={{ mt: 3, mb: 1.5, backgroundColor: '#55C4ED !important' }} />
            <Button
              onClick={() => openRelationshipDialog(relationshipDialog.field)}
              sx={{ p: 0, color: '#009BD8 !important', textTransform: 'none', fontWeight: 600 }}
            >
              <Box component="span" sx={{ fontSize: 22, fontWeight: 300, mr: 0.75 }}>+</Box>
              Add More
            </Button>

            <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 1.5 }}>
              <Button
                onClick={() => setRelationshipDialog((current) => ({ ...current, open: false }))}
                sx={{ color: '#37AEE0 !important', fontSize: '11px', fontWeight: 700, minWidth: 58 }}
              >
                CANCEL
              </Button>
              <Button
                onClick={saveRelationship}
                variant="contained"
                sx={{
                  minWidth: 100,
                  height: 32,
                  backgroundColor: '#37AEE0 !important',
                  color: '#FFFFFF !important',
                  fontSize: '11px',
                  fontWeight: 700,
                  '&:hover': { backgroundColor: '#1596CB' },
                }}
              >
                SAVE
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>

        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, field: 'successors', relationship: null })}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: '4px',
              overflow: 'hidden',
              backgroundColor: '#F9FCFF',
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: '#003771',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: 600,
              py: 1.25,
              pr: 2,
            }}
          >
            Deleting {deleteDialog.field === 'successors' ? 'Successor' : 'Predecessor'}
            <IconButton
              aria-label="Close delete confirmation"
              onClick={() => setDeleteDialog({ open: false, field: 'successors', relationship: null })}
              sx={{ color: '#FFFFFF', float: 'right', p: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 4, py: 3, backgroundColor: '#F9FCFF' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box component="img" src={DeleteImage} alt="" sx={{ width: 64, height: 64 }} />
              <Stack spacing={1}>
                <Typography sx={{ color: '#111111', fontSize: '20px', fontWeight: 600 }}>
                  Are you sure to delete {deleteDialog.field === 'successors' ? 'successor' : 'predecessor'}{' '}
                  {deleteDialog.relationship?.name}?
                </Typography>
                <Typography sx={{ color: '#333333', fontSize: '14px' }}>
                  Once confirmed, the action can&apos;t be undone.
                </Typography>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 4, pb: 3, backgroundColor: '#F9FCFF' }}>
            <Button
              onClick={() => setDeleteDialog({ open: false, field: 'successors', relationship: null })}
              sx={{ color: '#009BD8', fontWeight: 700 }}
            >
              NO, I WANT IT!
            </Button>
            <Button
              onClick={removeRelationship}
              variant="contained"
              sx={{
                backgroundColor: '#FF2B2B',
                color: '#FFFFFF',
                fontWeight: 700,
                '&:hover': { backgroundColor: '#D92121' },
              }}
            >
              DELETE
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box
      sx={{ mt: 3, padding: '20px', borderRadius: '8px' }}
    >
      <Typography variant="body2" sx={{ opacity: 0.7, mb: 4, px: 2, color: 'textLight.main' }}>
        ⓘ This section is to configure GDO.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 2, gap: 2 }}>
        <Typography
          variant="subHeader"
          sx={{
            color: 'rgba(0, 155, 216, 1)',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          GDO Name
        </Typography>
        <Divider
          sx={{ flexGrow: 1, backgroundColor: 'rgba(0, 155, 216, 1)' }}
        />
      </Box>

      <Grid container spacing={2} mb={2} px={2}>
        <Grid item xs={9}>
          <FormControl fullWidth error={isError}>
            {isGdoMode ? (
              <TextField
                placeholder="GDO-"
                value={sourceData.program}
                onChange={(e) => {
                  const value = e.target.value;
                  updateField('program', value);
                }}
                size="small"
                error={isError}
                helperText={helperText}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': { color: 'textLight.main' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: isError ? theme.palette.error.main : 'textLight.main',
                    },
                    '&:hover fieldset': {
                      borderColor: 'textLight.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'textLight.main',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: isError ? theme.palette.error.main : 'inherit',
                  },
                }}
              />
            ) : (
              <Select
                value={sourceData.program}
                onChange={(e) => {
                  const value = e.target.value;
                  updateField('program', value);
                }}
                displayEmpty
                size="small"
                sx={{
                  width: '100%',
                  border: `1px solid ${
                    isError ? theme.palette.error.main : theme.palette.textLight.main
                  }`,
                  '& .MuiInputBase-input': { color: 'textLight.main' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'textLight.main',
                    },
                    '&:hover fieldset': {
                      borderColor: 'textLight.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'textLight.main',
                    },
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select GDO Name
                </MenuItem>
                <MenuItem value="GDO-1">GDO-1</MenuItem>
                <MenuItem value="GDO-2">GDO-2</MenuItem>
                <MenuItem value="GDO-3">GDO-3</MenuItem>
                <MenuItem value="GDO-5">GDO-5</MenuItem>
              </Select>
            )}
          </FormControl>
          {!isGdoMode && <FormHelperText error={isError}>{helperText}</FormHelperText>}
        </Grid>
      </Grid>
    </Box>
  );
};
